import React, { useEffect, useState } from "react";
import { GetServerSideProps, NextPage } from "next";
import {
    CErrorDisplayer,
    CLogin,
    CNotFound,
    CompetitionOverview as Overview,
    CRelogin,
    CUnauthorized,
    OwnerOverview,
} from "@components/competition";
import { Header } from "@components";
import { getCompetition } from "@util/api/competitions";
import { COMPETITION_TABS } from "@util/constants";
import { CompetitionViewHandler } from "@components/competition";
import Head from "next/head";
import { useSelector } from "react-redux";
import { ICompData } from "@util/interfaces";
import { Box, Spinner, Text, useToast } from "@chakra-ui/react";
import { getDateStatus } from "@util/dateConverter";
import { useRouter } from "next/router";
import { IUser } from "@redux/slices/user";
import { X, XSquare } from "react-feather";

const CompetitionPage: NextPage = (props: {
    data: ICompData;
    children: JSX.Element;
    page_type: "unauthorized" | "login" | "data" | "relogin";
}) => {
    const router = useRouter();
    const toast = useToast();
    const user = useSelector((state: { user: IUser }) => state.user);

    const username = useSelector((state: { user: { data: { username: string } } }) => {
        return state.user.data.username;
    });
    const isOwner = props.data?.owner.username === username;
    const isCollaborator = props.data?.collaborators.some((value) => value.user.username === username);
    const collaboratorDetails = props.data?.collaborators.find((value) => value.user.username === username);
    const type = props.data?.is_classroom ? "کلاس درس" : "مسابقه";

    const [hasChanged, setHasChanged] = useState(false);
    const [compData, setCompData] = useState<ICompData>(props.data ?? null);

    const resetCompData = async () => {
        const serverResponse = (await getCompetition(router.query.competition, {
            toast,
            requesterPath: router.asPath,
        })) as Response;
        if (serverResponse.ok) {
            const cd = await serverResponse.json();
            setCompData(cd);
        } else {
            toast({
                status: "error",
                isClosable: true,
                duration: 9000,
                description: `تغییرات شما با موفقیت اعمال شد ولی در دریافت داده‌های ${type} از سرور مشکلی پیش آمده. شما می‌توانید صفحه را دوباره لود کنید یا به انجام تغییرات خود ادامه دهید.`,
            });
        }

        setHasChanged(false);
    };

    useEffect(() => {
        if (hasChanged) resetCompData();
    }, [hasChanged]);

    let currentViewObj: { label: string; key: string; icon: JSX.Element } = { key: "", icon: null, label: "" };
    Object.values(COMPETITION_TABS).forEach((el) => {
        if (el.key === "overview") {
            currentViewObj = el;
            return;
        }
    });
    let currentViewComponent: React.ReactElement = null;
    currentViewComponent = compData ? (
        isOwner || isCollaborator ? (
            <OwnerOverview
                description={compData.description}
                contentType={compData.content_type}
                coverImage={compData.cover_image}
                toast={toast}
                router={router}
            />
        ) : (
            <Overview
                description={compData.description}
                coverImage={compData.cover_image}
                contentType={compData.content_type}
            />
        )
    ) : (
        <Box className={`w-full items-center mt-8 flex felx-col`}>
            <Spinner />
        </Box>
    );
    const getPageContent = (
        page_type: "unauthorized" | "login" | "data" | "relogin" | "internal_error" | "not_found" | "not_participant",
    ) => {
        switch (page_type) {
            case "login":
                return <CLogin />;
            case "data":
                return (
                    <CompetitionViewHandler
                        data={compData}
                        currentViewComponent={currentViewComponent}
                        currentViewObj={currentViewObj}
                        isOwner={isOwner}
                        isCollaborator={isCollaborator}
                        collaborationAccessLevel={isCollaborator ? collaboratorDetails.access_level : undefined}
                        setHasChanged={setHasChanged}
                        toast={toast}
                        router={router}
                        user={user}
                    />
                );
            case "relogin":
                return <CRelogin />;
            case "unauthorized":
                return <CUnauthorized />;
            case "not_found":
                return <CNotFound />;
            case "not_participant":
                return (
                    <CErrorDisplayer
                        text={<Text fontSize={"xl"}>برای دیدن محتوای این صفحه باید در {type} شرکت کرده باشید</Text>}
                        icon={X}
                        hasButton={false}
                    />
                );
            case "internal_error":
                return (
                    <CErrorDisplayer
                        text={
                            <Text fontSize={"xl"} textAlign="center">
                                مشکلی در نمایش اطلاعات رخ داده است. <br /> لطفاً دوباره تلاش کنید.
                            </Text>
                        }
                        icon={XSquare}
                        hasButton={false}
                    />
                );
            default:
                return <Spinner size="sm" />;
        }
    };

    return (
        <>
            <Head>
                <title>RoboEpics | {props.page_type === "data" ? `${compData.title}` : props.page_type}</title>
                <meta name="viewport" content="initial-scale=1.0, width=device-width" />
            </Head>
            <Header toast={toast} router={router} user={user} />
            {getPageContent(props.page_type)}
        </>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params, res, req }) => {
    const serverResponse = await getCompetition(params.competition, { req, res });
    if (serverResponse instanceof Response) {
        if (serverResponse.ok) {
            const compData = await serverResponse.json();
            switch (compData.participation_type) {
                case 10:
                default:
                    compData.participation_type = "انفرادی";
                    break;
                case 30:
                    compData.participation_type = "انفرادی و تیمی";
                    break;
            }

            compData.date_published = getDateStatus(compData.date_published);
            return { props: { data: compData, page_type: "data" } };
        } else {
            switch (serverResponse.status) {
                case 401:
                    return { props: { data: null, page_type: "unauthorized" } };
                case 403:
                    return { props: { data: null, page_type: "not_participant" } };
                case 404:
                    return { props: { data: null, page_type: "not_found" } };
                case 500:
                case 400:
                default:
                    if (serverResponse.status === 400) console.log(await serverResponse.json());
                    return { props: { data: null, page_type: "internal_error" } };
            }
        }
    } else {
        return { redirect: serverResponse };
    }
};

export default CompetitionPage;
